import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';

/**
 * RouteAnnouncer - Announces route changes to screen readers.
 * Uses an aria-live region so assistive technologies can
 * inform users when the page changes.
 */
function getPageNameFromPath(pathname: string): string {
    const segments = pathname.split('/').filter(Boolean);
    if (segments.length === 0) return 'Home';
    return segments[segments.length - 1]
        .replace(/-/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function RouteAnnouncer(): JSX.Element {
    const location = useLocation();
    const [announcement, setAnnouncement] = useState('');
    const isFirstRender = useRef(true);

    useEffect(() => {
        // Don't announce on initial page load
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        const pageName = getPageNameFromPath(location.pathname);
        setAnnouncement(`Navigated to ${pageName}`);
    }, [location.pathname]);

    return (
        <div
            role="status"
            aria-live="polite"
            aria-atomic="true"
            style={{
                position: 'absolute',
                width: '1px',
                height: '1px',
                padding: 0,
                margin: '-1px',
                overflow: 'hidden',
                clip: 'rect(0, 0, 0, 0)',
                whiteSpace: 'nowrap',
                borderWidth: 0,
            }}
        >
            {announcement}
        </div>
    );
}
